import { isNullOrWhiteSpace } from "../Shared/Utilities/string";
import {
  getBookingSessionIdFromElement,
  saveBookingSessionIdToStorage
} from "./booking-session-id";
import { emitEvent } from "./emit-event";
import {
  EVENT_TYPE,
  LINK_SERVICE_TYPE,
  LINK_TYPE,
  LinkEventParameters,
  LinkServiceType
} from "./events";
import { getMetaContent, isBookingButton, loadUtmParams } from "./utilities";

// Helper function to determine booking service type
const determineBookingServiceType = (
  element: HTMLElement,
  href: string
): LinkServiceType | undefined => {
  if (element.dataset.vetstoriaBookingButton !== undefined) {
    return LINK_SERVICE_TYPE.vetstoria;
  }

  const pmsTypeString = (
    element.dataset.clinicPmsType ??
    getMetaContent("clinic-pms-type") ??
    href
  ).toLowerCase();

  if (pmsTypeString.includes("provet")) {
    return LINK_SERVICE_TYPE.provet;
  } else if (pmsTypeString.includes("vetstoria")) {
    return LINK_SERVICE_TYPE.vetstoria;
  } else if (pmsTypeString.includes("petsxl")) {
    return LINK_SERVICE_TYPE.petsXL;
  } else if (pmsTypeString.includes("petleo")) {
    return LINK_SERVICE_TYPE.petLeo;
  } else if (pmsTypeString.includes("abupet")) {
    return LINK_SERVICE_TYPE.abuPet;
  } else if (pmsTypeString.includes("sanimalis")) {
    return LINK_SERVICE_TYPE.sanimalis;
  }

  return LINK_SERVICE_TYPE.other;
};

// Helper function to set clinic parameters from element dataset with optional meta fallback
const setClinicParameters = (
  eventParams: LinkEventParameters,
  element: HTMLElement,
  useMetaFallback: boolean
): void => {
  eventParams.clinic_bid = !isNullOrWhiteSpace(element.dataset.clinicBid)
    ? element.dataset.clinicBid
    : undefined;
  eventParams.clinic_cms_id = !isNullOrWhiteSpace(element.dataset.clinicId)
    ? element.dataset.clinicId
    : undefined;
  eventParams.clinic_pms_id = !isNullOrWhiteSpace(element.dataset.clinicPmsId)
    ? element.dataset.clinicPmsId
    : undefined;
  eventParams.clinic_name = !isNullOrWhiteSpace(element.dataset.clinicName)
    ? element.dataset.clinicName
    : undefined;

  if (useMetaFallback) {
    eventParams.clinic_bid ||= getMetaContent("clinic-bid");
    eventParams.clinic_cms_id ||= getMetaContent("clinic-id");
    eventParams.clinic_pms_id ||= getMetaContent("clinic-pms-id");
    eventParams.clinic_name ||= getMetaContent("clinic-name");
  }
};

export const initializeClickTracker = () => {
  // Tracks all general link clicks on the site, with special handling for booking/referral buttons.
  // Note that link_click events are sent using the Beacon Web API to Jentis, and are guaranteed
  // to be sent even if the current page closes/navigates away (which typically happens when clicking a link).
  document.addEventListener("click", function (event) {
    const eventTarget = event.target as HTMLElement | null | undefined;

    const element =
      eventTarget?.closest<HTMLAnchorElement>("a[href]") ??
      eventTarget?.closest<HTMLElement>("[data-vetstoria-booking-button]");

    if (element) {
      // Note that we need to use getAttribute here to get the actual href value as written in the HTML,
      // rather than the fully resolved URL that element.href provides.
      // This is due to the fully resolved URL including the current domain for relative links.
      const href = element.getAttribute("href");
      const text = element.textContent?.trim() ?? "";

      const hrefUri = href ? new URL(href, window.location.href) : undefined;

      const block = element.closest<HTMLElement>("[data-block-id]");
      const contentTypeElement = element.closest<HTMLElement>("[data-content-type]");

      // Prepare base event parameters
      const eventParams: LinkEventParameters = {
        content_id: block?.dataset.blockId,
        content_type:
          block?.dataset.blockType ??
          contentTypeElement?.dataset.contentType ??
          "content",
        is_conversion: false,
        target_type: LINK_TYPE.other,
        target_url: href ?? "",
        text: text,
        campaign_name: getMetaContent("campaign-name")
      };

      // Check for data-booking-button attribute
      if (isBookingButton(element)) {
        setClinicParameters(eventParams, element, true);

        eventParams.target_type = LINK_TYPE.bookingService;
        eventParams.is_conversion = true;
        eventParams.target_service = determineBookingServiceType(element, href ?? "");

        const bookingSessionId = getBookingSessionIdFromElement(element);

        eventParams.form_session_id = bookingSessionId;

        const utmParams = loadUtmParams();

        eventParams.utm_channel = utmParams?.channel;
        eventParams.utm_medium = utmParams?.medium;
        eventParams.utm_source = utmParams?.source;

        const isInternal =
          hrefUri !== undefined && hrefUri.origin === window.location.origin;

        if (bookingSessionId && isInternal) {
          saveBookingSessionIdToStorage(bookingSessionId);
        }
      }
      // Check for data-emergency-button attribute
      else if (element.dataset.emergencyButton !== undefined) {
        eventParams.target_type = LINK_TYPE.emergency;
        eventParams.clinic_cms_id = hrefUri?.searchParams.get("clinic-id") ?? undefined;
      }
      // Check for data-treatment-link attribute
      else if (element.dataset.treatmentLink !== undefined) {
        eventParams.target_type = LINK_TYPE.treatment;
        eventParams.treatment_ccid = !isNullOrWhiteSpace(element.dataset.treatmentMccId)
          ? element.dataset.treatmentMccId
          : undefined;
      }
      // Check for data-clinic-link attribute
      else if (element.dataset.clinicLink !== undefined) {
        setClinicParameters(eventParams, element, false);

        eventParams.target_type = LINK_TYPE.clinic;
      }
      // Check for data-referral-button attribute
      else if (element.dataset.referralButton !== undefined) {
        setClinicParameters(eventParams, element, true);

        eventParams.target_type = LINK_TYPE.referral;
        eventParams.is_conversion = true;
      }
      // Check for phone and email links
      else if (href?.startsWith("tel:")) {
        setClinicParameters(eventParams, element, true);

        eventParams.target_type = LINK_TYPE.phone;
        eventParams.is_conversion = true;
      } else if (href?.startsWith("mailto:")) {
        setClinicParameters(eventParams, element, true);

        eventParams.target_type = LINK_TYPE.email;
        eventParams.is_conversion = true;
      }
      // Check for maps/directions links
      else if (
        href?.includes("www.google.com/maps") ||
        href?.includes("maps.google.com")
      ) {
        setClinicParameters(eventParams, element, true);

        eventParams.target_type = LINK_TYPE.directions;
      }
      // Check for cross-brand AniCura sites
      else if (href?.includes("https://www.anicura.")) {
        const linkDomain = new URL(href).hostname;
        const currentDomain = window.location.hostname;

        if (linkDomain !== currentDomain) {
          eventParams.target_type = LINK_TYPE.crossBrand;
        } else {
          eventParams.target_type = LINK_TYPE.sameSite;
        }
      }
      // Check for social media links
      else if (
        href?.includes("facebook.com") ||
        href?.includes("twitter.com") ||
        href?.includes("instagram.com") ||
        href?.includes("linkedin.com")
      ) {
        setClinicParameters(eventParams, element, true);

        eventParams.target_type = LINK_TYPE.social;

        // Determine social platform
        if (href.includes("facebook.com")) {
          eventParams.target_service = LINK_SERVICE_TYPE.facebook;
        } else if (href.includes("linkedin.com")) {
          eventParams.target_service = LINK_SERVICE_TYPE.linkedIn;
        } else if (href.includes("instagram.com")) {
          eventParams.target_service = LINK_SERVICE_TYPE.instagram;
        } else if (href.includes("//x.com") || href.includes("twitter.com")) {
          eventParams.target_service = LINK_SERVICE_TYPE.x;
        } else {
          eventParams.target_service = LINK_SERVICE_TYPE.other;
        }
      }
      // Differentiate same-site vs external links
      else if (
        href?.startsWith("/") ||
        href?.startsWith("#") ||
        !href?.startsWith("http")
      ) {
        eventParams.target_type = LINK_TYPE.sameSite;
      }
      // External links
      else {
        eventParams.target_type = LINK_TYPE.other;
      }

      emitEvent(EVENT_TYPE.linkClick, eventParams, true);
    }
  });
};
